// import { createPricingStructure, createConfigHash } from './solutionPricing.jsx';
import { getProductPrice } from './configUtilities';
import { getAllActivePartNumbers, getInstallationPartNumber, getDiscountPartNumber, getSoftwareLicensingModel } from './partNumberUtils.jsx';
import { log } from './devLog';

// Classic default: 10TB blocks, priced per subscription month. Term-based
// vendors (e.g. SMC) declare a different { unitTB, termBased } on the
// serviceOption via getSoftwareLicensingModel — see partNumberUtils.jsx.
const DEFAULT_SW_MODEL = { unitTB: 10, termBased: false };
const softwareUnits = (capacityTB, swModel) => Math.ceil(capacityTB / swModel.unitTB);
// Term-based SKUs bake the full term into the unit price — multiplier is 1.
const termMonths = (config, swModel) => (swModel.termBased ? 1 : config.subscriptionMonths);

// Re-exported per CONF-106 pt2 task-9: pricingCalculator.jsx is the
// established import surface for part-number lookups outside this module.
// NOTE: this returns the nested { nodes, drives, network, software, services }
// shape (see partNumberUtils.jsx) — App.js's exec-pricing wiring needs a flat
// Set of SKUs instead, so it imports the sibling getAllUniquePartNumbers
// directly from partNumberUtils.jsx rather than this export. See
// .superpowers/sdd/CONF-106-pt2/task-9-report.md for the deviation.
export { getAllActivePartNumbers };

/**
 * Calculate all software pricing and populate the software section (VduraCare, discounts, services)
 * @param {Object} pricing - The pricing structure to populate. This is mutated by this function.
 * @param {Object} config - Configuration object
 * @param {Object} productDb - Product database
 * @param {Object} metrics - Calculated metrics
 * @param {Object} solutionPricing - The pricing structure to populate. This is mutated by this function.
 * @param {Object} [vendor] - Vendor whose registry-declared licensing model (see
 *   getSoftwareLicensingModel) governs the software unit size and month
 *   multiplier for config.serviceOption. Omitting it defensively falls back to
 *   the classic model (legacy callers) — see DEFAULT_SW_MODEL above.
 */
export const calculateSoftwarePricing = (config, productDb, metrics, solutionPricing, vendor) => {
    // Only calculate if we have valid inputs
    if (!config || !productDb || !metrics) {
        return;
    }

    const partNumbers = getAllActivePartNumbers(config, productDb, metrics, vendor);
    const swModel = getSoftwareLicensingModel(config.serviceOption, vendor);

    // Software pricing assumes that software prices were already set in config object (config.ssdSoftware, config.hddSoftware)
    // VduraCare SSD pricing by type
    // DEV NOTE : We are not using the individual ssd pricing functions here. We are using the combined pricing function. This is because of rounding issues.
    //            When we use math.ceil we get a different number of units in the combined vs the individual pricing functions. This causes the total to be off by roughly 1 unit
    //            This could still be the right way to do it, but for now we are using the combined pricing function.
    solutionPricing.software.vduraCare.ssd.total = calculateCombinedSsdSoftware(config, metrics, swModel);
    // NOT USED AT THE MOMENT in the total ssd pricing
    solutionPricing.software.vduraCare.ssd.directors = calculateDirectorSsdSoftware(config, metrics, swModel);
    solutionPricing.software.vduraCare.ssd.hybrid = config.hybridEnabled ? calculateHybridSsdSoftware(config, metrics, swModel) : 0;
    solutionPricing.software.vduraCare.ssd.afa = config.afaEnabled ? calculateAfaSsdSoftware(config, metrics, swModel) : 0;


    // VduraCare HDD pricing (Only applies if hybrid is enabled and has HDDs)
    solutionPricing.software.vduraCare.hdd.hybrid = config.hybridEnabled && config.hybridHddCapacity > 0 ?
        calculateHybridHddSoftware(config, metrics, swModel) : 0;


    // Calculate the VDURACare Subcomponent prices
    const [ssdLicenseTotal, ssdSupportTotal] = calculateSsdSoftwareSubcomponents(
        config,
        productDb,
        metrics,
        partNumbers.software.vduraCareSubcomponents,
        solutionPricing.software.vduraCare.ssd.total,
        swModel);

    const [hddLicenseTotal, hddSupportTotal] = calculateHddSoftwareSubcomponents(
        config,
        productDb,
        metrics,
        partNumbers.software.vduraCareSubcomponents,
        solutionPricing.software.vduraCare.hdd.hybrid,
        swModel);
    
    // MORK TODO : In expansion, how are these prices handled - need to verify the breakdown is normal and doesn't affect the total pricing
    solutionPricing.software.vduraCareSubcomponents.ssd.license = ssdLicenseTotal;
    solutionPricing.software.vduraCareSubcomponents.ssd.support = ssdSupportTotal;
    solutionPricing.software.vduraCareSubcomponents.hdd.license = config.hybridEnabled && config.hybridHddCapacity > 0 ? hddLicenseTotal : 0;
    solutionPricing.software.vduraCareSubcomponents.hdd.support = config.hybridEnabled && config.hybridHddCapacity > 0 ? hddSupportTotal : 0;

    // Discounts
    solutionPricing.software.discounts.promotionalDiscount = calculatePromotionalDiscount(config, productDb, metrics);

    // Services
    solutionPricing.software.services.installation = calculateInstallationCost(config, productDb, metrics);

    solutionPricing.calculate(); // Recalculate all totals and metadata
    // Check if valid
    if (solutionPricing.validate()) {
                  log('Total cost:', solutionPricing.solution.grandTotal);
    } else {
        console.warn('Pricing validation failed');
                  log('Errors:', solutionPricing.metadata.errors);
    }
    return;
};

/**
 * Calculate all hardware pricing and populate the software section (nodes, ssds, hdds, network cards)
 * @param {Object} config - Configuration object
 * @param {Object} productDb - Product database
 * @param {Object} metrics - Calculated metrics
 * @param {Object} solutionPricing - The pricing structure to populate. This is mutated by this function.
 * @param {PriceResolver} prices - Resolved price source (src/pricing/priceResolver.ts).
 *   REQUIRED and deliberately undefaulted: a `= {}` default here made a missing
 *   argument look like "no overrides", which is how the optimizer shipped ranking
 *   candidates at catalog prices. Pass createPriceResolver(priceBook, quarter, {})
 *   to mean "no overrides" explicitly.
 * @param {Object} [vendor] - Vendor, threaded through to getAllActivePartNumbers so a
 *   non-AIC config (e.g. SMC) resolves its own drive/software part numbers rather than
 *   silently falling back to AIC's registry (see getAllActivePartNumbers's own doc comment).
 */
export const calculateHardwarePricing = (config, productDb, metrics, solutionPricing, prices, vendor) => {
    if (typeof prices?.of !== 'function') {
        throw new TypeError(
            'calculateHardwarePricing requires a PriceResolver. Pass ' +
            'createPriceResolver(priceBook, quarter, {}) if you mean "no overrides".');
    }
    // Only calculate if we have valid inputs
    if (!config || !productDb || !metrics) {
        return;
    }

    // TODO - calculate all the hardware pricing - this will take some more time. I might decide to split out part number calculations.
    const partNumbers = getAllActivePartNumbers(config, productDb, metrics, vendor);

    // Pricing gets passed in as a reference to the pricing structure
    calculateDirectorHWPricing(config, productDb, metrics, solutionPricing, partNumbers, prices.of);

    if (config.hybridEnabled) {
        calculateHybridHWPricing(config, productDb, metrics, solutionPricing, partNumbers, prices.of);
    }

    if (config.afaEnabled) {
        calculateAfaHWPricing(config, productDb, metrics, solutionPricing, partNumbers, prices.of);
    }


    solutionPricing.calculate(); // Recalculate all totals and metadata
    // Check if valid
    if (solutionPricing.validate()) {
                  log('Total cost:', solutionPricing.solution.grandTotal);
    } else {
        console.warn('Pricing validation failed');
                  log('Errors:', solutionPricing.metadata.errors);
    }
    return;
}

/*******************************************************
/ Helper functions for each calculation type
/ MORK TODO : All of these functions need to be reviewed when metric fixes are added. 
/ We recalculate these capacity values in the metrics calculations.
********************************************************/
const calculateDirectorSsdSoftware = (config, metrics, swModel = DEFAULT_SW_MODEL) => {
    // MORK TODO : Investigate... Velo Count is the number of directors??
    // metrics.totalVeloSsdCapacity is the same as directorSsdCapacity
    //const directorSsdCapacity = config.veloCount * config.ssdPerVelo * config.veloSsdCapacity;
    const directorSoftwareUnits = softwareUnits(metrics.totalVeloSsdCapacity, swModel);
    const directorSsdSoftwarePrice = directorSoftwareUnits * config.ssdSoftware * termMonths(config, swModel);
    return directorSsdSoftwarePrice;
};

const calculateHybridSsdSoftware = (config, metrics, swModel = DEFAULT_SW_MODEL) => {
    // MORK TODO : Investigate. Are we using ssdPerVpod and is it the same as vpodPerNode in config or is this an incorrectly named variable?
    // const hybridSsdCapacity = config.hybridNodeCount * config.ssdPerVpod * config.hybridVpodSsdCapacity;
    const hybridSoftwareUnits = softwareUnits(metrics.totalHybridSsdCapacityTB, swModel);
    const hybridSsdSoftwarePrice = hybridSoftwareUnits * config.ssdSoftware * termMonths(config, swModel);
    return hybridSsdSoftwarePrice;
};

const calculateAfaSsdSoftware = (config, metrics, swModel = DEFAULT_SW_MODEL) => {
    // MORK TODO : Investigate. Are we using ssdPerVpod and is it the same as vpodPerNode in config or is this an incorrectly named variable?
    // const afaSsdCapacity = config.afaNodeCount * config.ssdPerVpod * config.afaVpodSsdCapacity;
    const afaSoftwareUnits = softwareUnits(metrics.totalAfaRawCapacityTB, swModel);
    const afaSsdSoftwarePrice = afaSoftwareUnits * config.ssdSoftware * termMonths(config, swModel);
    return afaSsdSoftwarePrice;
};

// MORK TODO : 10 is a magic number for the classic model. We display units as
// 10 TB units in the UI/BOM/Quote for that model; term-based vendors use their
// own unitTB (see swModel / softwareUnits above).
// Combined SSD Software assumes all SSD capacity from all node types (D/F/S)
const calculateCombinedSsdSoftware = (config, metrics, swModel = DEFAULT_SW_MODEL) => {

    const totalSsdCapacity =   metrics.totalSsdCapacityTB;
    const ssdSoftwareUnits = softwareUnits(totalSsdCapacity, swModel);
    const ssdSoftwarePrice = ssdSoftwareUnits * config.ssdSoftware * termMonths(config, swModel);
    return ssdSoftwarePrice;
};

const calculateHybridHddSoftware = (config, metrics, swModel = DEFAULT_SW_MODEL) => {
    // const hybridHddCapacity = config.hybridNode * config.jbodSize * config.hybridHddCapacity;
    const hddSoftwareUnits = softwareUnits(metrics.totalHddCapacityTB, swModel);
    const hddSsdSoftwarePrice = hddSoftwareUnits * config.hddSoftware * termMonths(config, swModel);
    return hddSsdSoftwarePrice;
};

const calculateSsdSoftwareSubcomponents = (config, productDB, metrics, subcomponentPartNum, ssdSoftwareTotal, swModel = DEFAULT_SW_MODEL) => {
    const units = softwareUnits(metrics.totalSsdCapacityTB, swModel);
    // Support price is a static value from the productDB
    const softwareSupportUnitPrice = getProductPrice(productDB, config.quarter, subcomponentPartNum.ssd_support);
    // License price is the remainder of the total software unit price
    const softwareLicenseUnitPrice = config.ssdSoftware - softwareSupportUnitPrice;

    const softwareSupportTotal = units * softwareSupportUnitPrice * termMonths(config, swModel);
    const softwareLicenseTotal = ssdSoftwareTotal - softwareSupportTotal;

    return [softwareLicenseTotal, softwareSupportTotal];
}

const calculateHddSoftwareSubcomponents = (config, productDB, metrics, subcomponentPartNum, hddSoftwareTotal, swModel = DEFAULT_SW_MODEL) => {
    const units = softwareUnits(metrics.totalHddCapacityTB, swModel);
    // Support price is a static value from the productDB
    const softwareSupportUnitPrice = getProductPrice(productDB, config.quarter, subcomponentPartNum.hdd_support);
    // License price is the remainder of the total software unit price
    const softwareLicenseUnitPrice = config.hddSoftware - softwareSupportUnitPrice;

    const softwareSupportTotal = units * softwareSupportUnitPrice * termMonths(config, swModel);
    const softwareLicenseTotal = hddSoftwareTotal - softwareSupportTotal;

    return [softwareLicenseTotal, softwareSupportTotal];
}


// Promotional discount is a negative value applied to the total software cost
const calculatePromotionalDiscount = (config, productDb, metrics) => {
    const softwareDiscountRate = getProductPrice(productDb, config.quarter, getDiscountPartNumber());
    const totalSoftwareUnits = Math.ceil(metrics.totalRawCapacityTB / 10);
    const discountCost = config.discountMonths > 0 ? (-1 * config.discountMonths * softwareDiscountRate * totalSoftwareUnits) : 0;
    return discountCost;
};

// Installation cost is based on number of racks rounded up
const calculateInstallationCost = (config, productDb, metrics) => {
    const installationUnitCost = getProductPrice(productDb, config.quarter, getInstallationPartNumber());
    const rackCount = Math.ceil((metrics.totalRU || 0) / 42);
    const installationCost = rackCount * installationUnitCost;
    return installationCost;
};

// Calculate hardware pricing for directors (nodes, ssds, network cards)
const calculateDirectorHWPricing = (config, productDb, metrics, solutionPricing, partNumbers, priceOf) => {
    const directorInfo = partNumbers.nodes.director;
    const directorDrives = partNumbers.drives.director;
    const directorNetwork = partNumbers.network.director;

    // Director Nodes
    solutionPricing.hardware.directors.nodes = config.veloCount * priceOf(directorInfo.partNumber);

    // Director SSDs
    solutionPricing.hardware.directors.ssds = config.veloCount * productDb.ssdPerVelo * priceOf(directorDrives.ssdPartNumber);

    // Director Network Cards. CONF-93: single-port options ship an OCP *and* a
    // PCIe card per director (the CCI "2x SP" bundles), so this line covers the
    // director's cards as a whole. Dual-port options have no `pcie` block and
    // the arithmetic is unchanged.
    solutionPricing.hardware.directors.networkCards = config.veloCount
        * (priceOf(directorNetwork.ocp.partNumber)
           + (directorNetwork.pcie ? priceOf(directorNetwork.pcie.partNumber) : 0));
};

// Calculate hardware pricing for hybrid (nodes, ssds, hdds/jbods, network cards)
const calculateHybridHWPricing = (config, productDb, metrics, solutionPricing, partNumbers, priceOf) => {
    const hybridNode = partNumbers.nodes.hybrid;
    const hybridDrives = partNumbers.drives.hybrid;
    const hybridNetwork = partNumbers.network.hybrid;

    // Hybrid Storage Nodes (S Nodes)
    solutionPricing.hardware.hybrid.nodes = config.hybridNodeCount * priceOf(hybridNode.partNumber);

    // Hybrid SSDs
    solutionPricing.hardware.hybrid.ssds = config.hybridNodeCount * productDb.ssdPerVpod * priceOf(hybridDrives.ssdPartNumber);

    // Hybrid HDDs (JBODs)
    solutionPricing.hardware.hybrid.hdds = config.hybridNodeCount * priceOf(hybridDrives.hddPartNumber);

    // Hybrid Network Cards (PCIe and OCP)
    solutionPricing.hardware.hybrid.networkCards = config.hybridNodeCount * (
        priceOf(hybridNetwork.pcie.partNumber) +
        priceOf(hybridNetwork.ocp.partNumber)
    );
};

// Calculate hardware pricing for AFA (nodes, ssds, network cards)
const calculateAfaHWPricing = (config, productDb, metrics, solutionPricing, partNumbers, priceOf) => {
    const afaNode = partNumbers.nodes.afa;
    const afaDrives = partNumbers.drives.afa;
    const afaNetwork = partNumbers.network.afa;

    // AFA Storage Nodes (F Nodes)
    solutionPricing.hardware.afa.nodes = config.afaNodeCount * priceOf(afaNode.partNumber);

    // AFA SSDs
    solutionPricing.hardware.afa.ssds = config.afaNodeCount * productDb.ssdPerVpod * priceOf(afaDrives.ssdPartNumber);

    // AFA Network Cards (PCIe and OCP). CONF-210: the counts are CARDS, not
    // nodes — the Dual CPU flash node carries two PCIe cards per node — so
    // each kind is booked as its own count x price.
    solutionPricing.hardware.afa.networkCards =
        afaNetwork.pcie.count * priceOf(afaNetwork.pcie.partNumber) +
        afaNetwork.ocp.count * priceOf(afaNetwork.ocp.partNumber);
};

